import {
  AccountTransaction,
  AccountTransactionChangeSummary,
  DailyCountList,
  Debt,
  DebtPayment,
  GetAccountsByStore,
  InventoryChange,
  InventoryChangeReason,
  PurchaseOrder,
  SoftDeleteFlag,
  SortOrder,
  StoreInventoryValue,
  StoreProductChange,
  StoreProductChangeEvents,
  Supplier,
} from '@bofrak-backend/shared';
import { useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useQuery } from 'react-query';
import { apiAdapter } from '../api/backend';
import { dateRangeAtom, merchantAtom, storeAtom } from '../recoil/atoms';
import { useSalesReportStore } from './use-sales-report-sections';
import { toQueryStatus } from '../utils/query-status';

/** Enough points for the inventory chart and history on a phone. */
const INVENTORY_VALUE_MAX_POINTS = 200;

/** Reasons to exclude from "inventory gain / loss". */
const EXCLUDED_REASONS = new Set<InventoryChangeReason>([
  InventoryChangeReason.SALE,
  InventoryChangeReason.REFUND,
  InventoryChangeReason.PURCHASE_ORDER,
]);

/** Paginate through ALL inventory changes in a date range, de-duplicated by id. */
async function fetchAllInventoryChanges(params: {
  merchant_id: string;
  store_id: string;
  start_date: string;
  end_date: string;
}): Promise<InventoryChange[]> {
  const all: InventoryChange[] = [];
  let cursor: string | undefined;
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getInventoryChanges({
      merchant_id: params.merchant_id,
      getInventoryChangesDto: {
        merchant_id: params.merchant_id,
        store_id: params.store_id,
        start_date: params.start_date,
        end_date: params.end_date,
        by_store_and_date_range: true,
        limit: 250,
        sort_order: SortOrder.DESC,
        cursor,
      },
    });
    if (res?.changes?.length) all.push(...res.changes);
    cursor = res?.cursor;
    hasMore = !!cursor && !!res?.changes?.length;
  }

  const unique = new Map<string, InventoryChange>();
  for (const c of all) {
    if (!unique.has(c.id)) unique.set(c.id, c);
  }
  return Array.from(unique.values());
}

/** Paginate through all debts matching the given params. */
async function fetchAllDebts(
  params: Parameters<typeof apiAdapter.getDebts>[0],
): Promise<Debt[]> {
  let cursor: string | undefined;
  let all: Debt[] = [];
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getDebts({ ...params, cursor, limit: 250 });
    all = [...all, ...res.debts];
    cursor = res.cursor;
    hasMore = !!cursor;
  }
  return all;
}

/** Paginate through all debt payments matching the given params. */
async function fetchAllDebtPayments(
  params: Parameters<typeof apiAdapter.getDebtPayments>[0],
): Promise<DebtPayment[]> {
  let cursor: string | undefined;
  const all: DebtPayment[] = [];
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getDebtPayments({
      ...params,
      cursor,
      limit: 250,
    });
    if (res.payments?.length) all.push(...res.payments);
    cursor = res.cursor;
    hasMore = !!cursor && !!res.payments?.length;
  }
  return all;
}

/** Paginate through all purchase orders matching the given params. */
async function fetchAllPurchaseOrders(
  merchant_id: string,
  params: Parameters<typeof apiAdapter.getAllPurchaseOrders>[1],
): Promise<PurchaseOrder[]> {
  const all: PurchaseOrder[] = [];
  let cursor: string | undefined;
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getAllPurchaseOrders(merchant_id, {
      ...params,
      cursor,
      limit: 250,
    });
    all.push(...res.purchase_orders);
    cursor = res.cursor ?? undefined;
    hasMore = !!cursor;
  }
  return all;
}

/** Paginate through all account transactions for a store in a date range. */
async function fetchAllAccountTransactions(
  params: GetAccountsByStore,
): Promise<AccountTransactionChangeSummary> {
  const all: AccountTransaction[] = [];
  const reversals: AccountTransaction[] = [];
  let cursor: string | undefined;
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getAccountTransactionsByStore({
      ...params,
      cursor,
      limit: 250,
    });
    if (res.items?.length) all.push(...res.items);
    if (res.reversals?.length) reversals.push(...res.reversals);
    cursor = res.cursor ?? undefined;
    hasMore = !!cursor;
  }
  return { items: all, summary: [], count: all.length, reversals };
}

/** Paginate through all suppliers for a merchant. */
async function fetchAllSuppliers(merchantId: string): Promise<Supplier[]> {
  const all: Supplier[] = [];
  let cursor: string | undefined;
  let hasMore = true;
  while (hasMore) {
    const page = await apiAdapter.getSuppliers({
      merchant_id: merchantId,
      cursor,
      limit: 250,
      include_deleted: SoftDeleteFlag.EXCLUDE_DELETED,
    });
    if (page.suppliers?.length) all.push(...page.suppliers);
    cursor = page.cursor ?? undefined;
    hasMore = !!cursor;
  }
  return all;
}

/**
 * Paginate through all cost-updated store product change records in a date
 * range. Records carry `inventory_value_change` when the products service
 * captured the revaluation impact of a manual parent cost edit.
 */
async function fetchAllCostUpdateChanges(params: {
  merchant_id: string;
  store_id: string;
  start_date: string;
  end_date: string;
}): Promise<StoreProductChange[]> {
  const all: StoreProductChange[] = [];
  let cursor: string | undefined;
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getStoreProductChanges({
      merchant_id: params.merchant_id,
      storeProductChangeDto: {
        by_store_and_event_and_date_range: true,
        store_id: params.store_id,
        event: StoreProductChangeEvents.STORE_PRODUCT_COST_UPDATED,
        start_date: params.start_date,
        end_date: params.end_date,
        limit: 250,
        sort_order: SortOrder.DESC,
        cursor,
      },
    });
    if (res?.changes?.length) all.push(...res.changes);
    cursor = res?.cursor;
    hasMore = !!cursor && !!res?.changes?.length;
  }
  return all;
}

/** Paginate through all daily count lists in a date range. */
async function fetchAllDailyCountLists(params: {
  merchant_id: string;
  store_id: string;
  start_date: string;
  end_date: string;
}): Promise<DailyCountList[]> {
  const all: DailyCountList[] = [];
  let cursor: string | undefined | null;
  let hasMore = true;
  while (hasMore) {
    const res = await apiAdapter.getDailyCountLists({
      merchant_id: params.merchant_id,
      store_id: params.store_id,
      start_date: params.start_date,
      end_date: params.end_date,
      limit: 250,
      cursor: cursor ?? undefined,
    });
    if (res?.daily_count_lists?.length) all.push(...res.daily_count_lists);
    cursor = res?.cursor;
    hasMore = !!cursor && !!res?.daily_count_lists?.length;
  }
  return all;
}

export type DashboardStatus = ReturnType<typeof useDashboardQueries>['status'];

export function useDashboardQueries() {
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);

  const hasIds = !!store?.id && !!merchant?.id;
  const hasRange = hasIds && !!dateRange.start && !!dateRange.end;

  // ── Sales report (store totals only; employees/products load on demand) ──
  const salesReportQuery = useSalesReportStore();
  const {
    data: salesReport,
    error: salesReportError,
    refetch: refetchSalesReport,
    isFetching: isFetchingSalesReport,
  } = salesReportQuery;

  // ── Inventory value changes over the selected date range ──
  const inventoryValueQuery = useQuery(
    ['inventoryValueDateRange', dateRange, store?.id, merchant?.id],
    () =>
      apiAdapter.getStoreInventoryValueDateRange({
        merchant_id: merchant!.id,
        store_id: store!.id,
        from: dateRange.start!.toISOString(),
        to: dateRange.end!.toISOString(),
        // Long ranges come back sampled (exact start and end values).
        max_points: INVENTORY_VALUE_MAX_POINTS,
      }),
    { enabled: hasRange },
  );
  const inventoryValueDateRange = inventoryValueQuery.data;

  // Derive start / end inventory values from the date-range snapshots.
  // The API returns snapshots ordered chronologically (ScanIndexForward: true),
  // so the first element is the earliest and the last is the most recent.
  const inventoryValueStart: StoreInventoryValue | undefined = useMemo(
    () => inventoryValueDateRange?.inventory_values?.[0],
    [inventoryValueDateRange],
  );
  const inventoryValue: StoreInventoryValue | undefined = useMemo(() => {
    const values = inventoryValueDateRange?.inventory_values;
    return values && values.length > 0 ? values[values.length - 1] : undefined;
  }, [inventoryValueDateRange]);

  // ── Debts in date range ──
  const debtsQuery = useQuery(
    ['debts', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllDebts({
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
        by_store_and_date_range: true,
      }),
    { enabled: hasRange },
  );
  const debts = debtsQuery.data;

  // ── All store debts (for point-in-time valuation) ──
  const allStoreDebtsQuery = useQuery(
    ['allStoreDebts', store?.id, merchant?.id],
    () =>
      fetchAllDebts({
        merchant_id: merchant!.id,
        store_id: store!.id,
        by_store: true,
      }),
    { enabled: hasIds },
  );
  const allStoreDebts = allStoreDebtsQuery.data;

  // ── Debt payments in date range (authoritative ledger for the client-debts
  //    valuation identity). Unlike account transactions, this includes
  //    non-money settlements (payment_type DEBT, e.g. debts extinguished by a
  //    refund receipt) and payment reversals, so outstanding-balance math
  //    reconciles exactly with the debts service. ──
  const debtPaymentsQuery = useQuery(
    ['debtPaymentsRange', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllDebtPayments({
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
        by_store_and_date_range: true,
      }),
    { enabled: hasRange },
  );
  const debtPaymentsInRange = debtPaymentsQuery.data;

  // ── Purchase order payments in date range ──
  const { data: purchaseOrderPayments } = useQuery(
    ['purchaseOrderPayments', dateRange, store?.id, merchant?.id],
    () =>
      apiAdapter.getAllPurchaseOrderPayments(merchant!.id, {
        by_date: true,
        store_id: store?.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
        merchant_id: merchant!.id,
        limit: 3000,
      }),
    { enabled: hasRange },
  );

  // ── Purchase orders created in date range ──
  const purchaseOrdersQuery = useQuery(
    ['purchaseOrders', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllPurchaseOrders(merchant!.id, {
        merchant_id: merchant!.id,
        store_id: store!.id,
        by_store_and_date: true,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
      }),
    { enabled: hasRange },
  );
  const purchaseOrders = purchaseOrdersQuery.data;

  // ── All purchase order payments (for valuation) ──
  const { data: allPurchaseOrderPayments } = useQuery(
    ['allPOPayments', store?.id, merchant?.id],
    () =>
      apiAdapter.getAllPurchaseOrderPayments(merchant!.id, {
        by_store: true,
        store_id: store?.id,
        merchant_id: merchant!.id,
        limit: 3000,
      }),
    { enabled: hasIds },
  );

  // ── Supplier valuation (backend-computed) ──
  const supplierValuationQuery = useQuery(
    ['supplierValuation', dateRange, store?.id, merchant?.id],
    () =>
      apiAdapter.getSupplierValuation(merchant!.id, {
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
      }),
    { enabled: hasRange },
  );
  const supplierValuation = supplierValuationQuery.data;

  // ── All suppliers (for name lookups) ──
  const { data: allSuppliers } = useQuery(
    ['allSuppliers', merchant?.id],
    () => fetchAllSuppliers(merchant!.id),
    { enabled: !!merchant?.id, staleTime: 5 * 60 * 1000 },
  );

  const suppliersMap = useMemo(() => {
    const map = new Map<string, Supplier>();
    for (const s of allSuppliers ?? []) {
      map.set(s.id.toLowerCase(), s);
    }
    return map;
  }, [allSuppliers]);

  // ── Store snapshots at start / end of period (available money) ──
  const snapshotsStartQuery = useQuery(
    ['storeSnapshotsStart', dateRange.start, store?.id, merchant?.id],
    () =>
      apiAdapter.getStoreSnapshotsAtTime({
        merchant_id: merchant!.id,
        store_id: store!.id,
        at_time: dateRange.start!.toISOString(),
      }),
    { enabled: hasIds && !!dateRange.start },
  );
  const snapshotsStart = snapshotsStartQuery.data;

  const snapshotsEndQuery = useQuery(
    ['storeSnapshotsEnd', dateRange.end, store?.id, merchant?.id],
    () =>
      apiAdapter.getStoreSnapshotsAtTime({
        merchant_id: merchant!.id,
        store_id: store!.id,
        at_time: dateRange.end!.toISOString(),
      }),
    { enabled: hasIds && !!dateRange.end },
  );
  const snapshotsEnd = snapshotsEndQuery.data;

  // ── Account transactions ──
  const accountTransactionsQuery = useQuery<AccountTransactionChangeSummary>(
    ['accountSummary', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllAccountTransactions({
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
      }),
    { enabled: hasRange },
  );
  const accountTransactions = accountTransactionsQuery.data;

  // ── Inventory changes for gain/loss (excludes Sales & Refunds) ──
  const inventoryChangesQuery = useQuery(
    ['allInventoryChanges', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllInventoryChanges({
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
      }),
    { enabled: hasRange },
  );
  const allInventoryChanges = inventoryChangesQuery.data;

  // ── Cost edits in date range (for the Cost Changes breakdown row) ──
  const costUpdatesQuery = useQuery(
    ['costUpdateChanges', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllCostUpdateChanges({
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
      }),
    { enabled: hasRange },
  );
  const costUpdateChanges = costUpdatesQuery.data;

  // ── Daily count lists for the date range ──
  const { data: dailyCountLists, isLoading: isLoadingDailyCounts } = useQuery(
    ['dailyCountLists', dateRange, store?.id, merchant?.id],
    () =>
      fetchAllDailyCountLists({
        merchant_id: merchant!.id,
        store_id: store!.id,
        start_date: dateRange.start!.toISOString(),
        end_date: dateRange.end!.toISOString(),
      }),
    { enabled: hasRange && !!store?.daily_count_enabled },
  );

  const inventoryChangesForGainLoss = useMemo(
    () => allInventoryChanges?.filter((c) => !EXCLUDED_REASONS.has(c.reason)),
    [allInventoryChanges],
  );

  // Per-request state: each number waits only for (and fails only with) the
  // request(s) it is computed from, never for the slowest one on the tab.
  const status = {
    salesReport: toQueryStatus(salesReportQuery),
    inventoryValues: toQueryStatus(inventoryValueQuery),
    debts: toQueryStatus(debtsQuery),
    allStoreDebts: toQueryStatus(allStoreDebtsQuery),
    debtPayments: toQueryStatus(debtPaymentsQuery),
    purchaseOrders: toQueryStatus(purchaseOrdersQuery),
    supplierValuation: toQueryStatus(supplierValuationQuery),
    snapshotsStart: toQueryStatus(snapshotsStartQuery),
    snapshotsEnd: toQueryStatus(snapshotsEndQuery),
    accountTransactions: toQueryStatus(accountTransactionsQuery),
    inventoryChanges: toQueryStatus(inventoryChangesQuery),
    costUpdates: toQueryStatus(costUpdatesQuery),
  };

  return {
    merchant,
    store,
    dateRange,
    setDateRange,
    // Raw data
    salesReport,
    inventoryValue,
    inventoryValueStart,
    inventoryValueDateRange,
    debts,
    allStoreDebts,
    debtPaymentsInRange,
    purchaseOrders,
    purchaseOrderPayments,
    allPurchaseOrderPayments,
    supplierValuation,
    suppliersMap,
    snapshotsStart,
    snapshotsEnd,
    accountTransactions,
    inventoryChangesForGainLoss,
    allInventoryChanges,
    costUpdateChanges,
    dailyCountLists,
    // Loading / failure state per request
    status,
    isLoadingDailyCounts,
    // Sales report failure (never render a failed fetch as MZN 0)
    salesReportError,
    refetchSalesReport,
    isFetchingSalesReport,
  };
}
